import { Button, Collapse, TextInput } from "@mantine/core";
import React, { useEffect, useState, useMemo } from "react";
import "./Logistics.css";
import { DateInput } from "@mantine/dates";
import B2BButton from "../components/common/B2BButton";
import notify from "../utils/Notification";
import { createB2BAPI } from "../api/Interceptor";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../api/EndPoints";
import { IconChevronDown, IconChevronUp, IconDownload } from "@tabler/icons-react";

const EMPTY_FORM = {
    transporterName: "",
    logesticReciptNo: "",
    lrReceiptDate: null,
    containerNumber: "",
    cartonNumber: "",
    ewayBillNo: "",
    vehicleNo: "",
    documentNo: "",
    trackingId: "",
    mobileNo: "",
    deliveryDate: null,
    shipmentDate: null,
    lrDocument: null,
};

const Logistics = ({ selectedStatusType, purchaseOrder, activeTabTop, fetchAllOrders }) => {
    const navigate = useNavigate();
    const B2B_API = useMemo(() => createB2BAPI(navigate), [navigate]);
    const [openLogistics, setOpenLogistics] = useState(false);
    const [formData, setFormData] = useState(EMPTY_FORM);

    const isDisabled = !!purchaseOrder?.lrEntryDone;
    const isWholesale = purchaseOrder?.orderType === "WHOLESALE";

    useEffect(() => {
        setOpenLogistics(false);
        setFormData(EMPTY_FORM);
    }, [purchaseOrder?.asnNo]);

    const handleToggleLogistics = () => {
        if (!openLogistics) {
            const ship = purchaseOrder?.shipment;
            if (ship) {
                setFormData({
                    transporterName: ship.transporterName || "",
                    logesticReciptNo: ship.logesticReciptNo || "",
                    lrReceiptDate: ship.lrReceiptDate ? new Date(ship.lrReceiptDate).toISOString().split("T")[0] : null,
                    containerNumber: ship.containerNumber || "",
                    cartonNumber: ship.cartonNumber || "",
                    ewayBillNo: ship.ewayBillNo || "",
                    vehicleNo: ship.vehicleNo || "",
                    documentNo: ship.documentNo || "",
                    trackingId: ship.trackingId || "",
                    mobileNo: ship.mobileNo || "",
                    deliveryDate: ship.deliveryDate ? new Date(ship.deliveryDate).toISOString().split("T")[0] : null,
                    shipmentDate: ship.shipmentDate ? new Date(ship.shipmentDate).toISOString().split("T")[0] : null,
                    lrDocument: null,
                });
            } else {
                setFormData(EMPTY_FORM);
            }
        } else if (!isDisabled) {
            setFormData(EMPTY_FORM);
        }
        setOpenLogistics((prev) => !prev);
    };

    const handleChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const LogisticsFields = useMemo(
        () => [
            {
                type: "TextInput",
                label: isWholesale
                    ? <span>Transporter Name <span style={{ color: "red" }}>*</span></span>
                    : <span>Transporter Name / Courier Name <span style={{ color: "red" }}>*</span></span>,
                name: "transporterName",
                placeholder: "Enter Transporter/Courier Name",
            },
            {
                type: "TextInput",
                label: isWholesale
                    ? <span>LR No <span style={{ color: "red" }}>*</span></span>
                    : <span>LR No / DOC.NO <span style={{ color: "red" }}>*</span></span>,
                name: "logesticReciptNo",
                placeholder: "Enter LR No",
            },
            {
                type: "Date",
                label: isWholesale
                    ? <span>LR Date <span style={{ color: "red" }}>*</span></span>
                    : <span>LR Date / Courier Date <span style={{ color: "red" }}>*</span></span>,
                name: "lrReceiptDate",
            },
            isWholesale
                ? { type: "TextInput", label: "NO OF Carton / Packet Count", name: "containerNumber", placeholder: "Enter Carton / Packet " }
                : { type: "TextInput", label: <span>No Of Carton / Packet Count <span style={{ color: "red" }}>*</span></span>, name: "cartonNumber", placeholder: "Enter Carton No" },
            { type: "TextInput", label: "E-Way Bill No", name: "ewayBillNo", placeholder: "Enter E-Way Bill No" },
            { type: "TextInput", label: "Vehicle No", name: "vehicleNo", placeholder: "Enter Vehicle No" },
            { type: "TextInput", label: "Invoice No", name: "documentNo", placeholder: "Enter Invoice / Document No" },
            { type: "TextInput", label: "Tracking Id", name: "trackingId", placeholder: "Enter Tracking Id" },
            { type: "TextInput", label: "Mobile Number", name: "mobileNo", placeholder: "Enter Mobile No" },
            { type: "Date", label: "Delivery Date", name: "deliveryDate" },
            { type: "Date", label: "Shipment Date", name: "shipmentDate" },
            purchaseOrder?.shipment?.lrDocumentUrl
                ? { type: "download", label: "LR Document", name: "lrDocument" }
                : { type: "fileInput", label: "LR Document", name: "lrDocument" },
        ],
        [purchaseOrder?.shipment?.lrDocumentUrl, isWholesale]
    );

    const requiredFieldNames = [
        "transporterName",
        "logesticReciptNo",
        "lrReceiptDate",
        ...(isWholesale ? [] : ["cartonNumber"]),
    ];
    const saveEnabled = requiredFieldNames.every((field) => {
        const val = formData[field];
        return val !== null && val !== undefined && val !== "";
    });

    const renderField = (field) => {
        switch (field.type) {
            case "TextInput":
                return (
                    <TextInput
                        key={field.name}
                        value={formData[field.name] ?? ""}
                        label={<span className="logistics-label">{field.label}</span>}
                        disabled={isDisabled}
                        placeholder={field.placeholder}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                    />
                );
            case "Date":
                return (
                    <DateInput
                        key={field.name}
                        value={formData[field.name] ? new Date(formData[field.name]) : null}
                        valueFormat="DD/MM/YYYY"
                        label={<span className="logistics-label">{field.label}</span>}
                        disabled={isDisabled}
                        placeholder="Pick a date"
                        onChange={(val) => handleChange(field.name, val ? val.toISOString().split("T")[0] : null)}
                    />
                );
            case "fileInput":
                return (
                    <div key={field.name} className="logistics-field">
                        <label className="logistics-label">
                            {field.label}
                        </label>

                        <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            disabled={isDisabled}
                            onChange={(e) => handleChange(field.name, e.target.files[0])}
                            className="logistics-file-input"
                        />
                    </div>
                );
            case "download":
                return (
                    <div key={field.name} className="logistics-field">
                        <label className="logistics-label">
                            {field.label}
                        </label>

                        <Button
                            fullWidth
                            color="#8BC53F"
                            rightSection={
                                <IconDownload size={16} className="logistics-download-btn" />
                            }
                            className="logistics-download-btn"
                            onClick={() =>
                                downloadDocument(purchaseOrder?.shipment?.lrDocumentUrl)
                            }
                        >
                            Download
                        </Button>
                    </div>
                );
            default:
                return null;
        }
    };

    const handleSaveLog = async () => {
        try {
            const { lrDocument, ...shipmentWithoutFile } = formData;

            const formDataPayload = new FormData();

            if (lrDocument instanceof File) {
                formDataPayload.append("lrDocument", lrDocument);
            }

            formDataPayload.append(
                "shipment",
                new Blob(
                    [JSON.stringify({ 
                        ...shipmentWithoutFile, 
                        asnNo: purchaseOrder?.asnNo,
                        asnNos: purchaseOrder?.groupedAsnNos ? purchaseOrder?.groupedAsnNos.split(", ") : [purchaseOrder?.asnNo]
                    })],
                    { type: "application/json" }
                )
            );

            await B2B_API.post(`asn/updateAsn`, { body: formDataPayload }).json();

            notify({
                error: false,
                success: true,
                title: "Logistics details saved successfully",
            });

            setFormData(EMPTY_FORM);
            setOpenLogistics(false);
            if (fetchAllOrders) fetchAllOrders();
        } catch (error) {
            console.error("Error on saving logistics:", error);
            notify({
                error: true,
                success: false,
                title: error?.response?.data?.message || "Failed to save logistics details",
            });
        }
    };

    async function downloadDocument(filePath) {
        try {
            const token = localStorage.getItem("token");
            const path = typeof filePath === "string" ? filePath : filePath?.uri;
            const fileName = path?.split("/").pop();

            const response = await fetch(
                `${BASE_URL}/vendor/download?filePath=${encodeURIComponent(path)}`,
                { method: "GET", headers: { Authorization: `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Failed to download file");

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            notify({ success: true, title: "LR Document downloaded successfully" });
        } catch (error) {
            console.error("Download error:", error);
            notify({ error: true, title: "Failed to download LR Document" });
        }
    }

    return (
        <>
            <div className="Logistic-view">
                {selectedStatusType !== "NEW" && activeTabTop === "ASN" && (
                    <Button
                        onClick={handleToggleLogistics}
                        style={{ background: "#ab1fa4ff" }}
                    >
                        LR Entry
                        <span style={{ marginLeft: "8px", display: "flex", alignItems: "center" }}>
                            {openLogistics ? (
                                <IconChevronDown size={16} />
                            ) : (
                                <IconChevronUp size={16} />
                            )}
                        </span>
                    </Button>
                )}
                {openLogistics && !isDisabled && (
                    <div style={{ margin: "0rem 2rem 1rem 1rem", display: "flex", justifyContent: "flex-end" }}>
                        <B2BButton
                            style={{ color: "black" }}
                            onClick={handleSaveLog}
                            disabled={!saveEnabled}
                            color="#8BC53F"
                            name="Submit LR"
                        />
                    </div>
                )}
            </div>

            <Collapse in={openLogistics} transitionDuration={300}>
                <div className="Logistic-fields">
                    {LogisticsFields.map((field) => renderField(field))}
                </div>
            </Collapse>
        </>
    );
};

export default Logistics;
